import { Injectable, inject, effect, signal, computed } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { SiteSettingsApiClient } from '@math-faculty/api-client';

@Injectable()
export class SiteTitleService {
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly siteSettingsClient = inject(SiteSettingsApiClient);

  private readonly pageTitle = signal<string | null>(null);
  private readonly isHomePage = signal<boolean>(false);
  private readonly description = signal<string | null>(null);
  private readonly seoTitle = signal<string | null>(null);

  private readonly siteName = toSignal(this.siteSettingsClient.getSiteName(), {
    initialValue: 'Математический факультет ЯрГУ',
  });

  private readonly defaultHomePageTitle = toSignal(
    this.siteSettingsClient.getDefaultHomePageTitle(),
    { initialValue: 'Главная страница сайта Математического факультета ЯрГУ' },
  );

  private readonly titleDelimiter = toSignal(this.siteSettingsClient.getTitleDelimiter(), {
    initialValue: ' | ',
  });

  private readonly nonHomePageTitle = computed(() => {
    return this.seoTitle() ?? this.pageTitle();
  });

  private readonly noPageTitle = computed(() => {
    if (this.isHomePage()) {
      return `${this.defaultHomePageTitle()}${this.titleDelimiter()}${this.siteName()}`;
    }

    return this.siteName();
  });

  private readonly metaTitle = computed(() => {
    if (this.isHomePage()) {
      return this.defaultHomePageTitle();
    }

    return this.nonHomePageTitle();
  });

  private readonly metaDescription = computed(() => {
    if (this.isHomePage()) {
      return 'Главная страница сайта Математического факультета ЯрГУ';
    }

    return this.description() ?? '';
  });

  private readonly fullTitle = computed(() => {
    const pageTitle = this.pageTitle();

    if (pageTitle) {
      return `${pageTitle}${this.titleDelimiter()}${this.siteName()}`;
    }

    return this.noPageTitle();
  });

  constructor() {
    this.setTitleUpdater();
  }

  setHomeTitle(): void {
    this.isHomePage.set(true);
    this.pageTitle.set(null);
  }

  setTitle(title: string | null): void {
    this.isHomePage.set(false);
    this.pageTitle.set(title);
  }

  setSeoTitle(seoTitle: string | null): void {
    this.seoTitle.set(seoTitle);
  }

  setSeoDescription(description: string | null): void {
    this.description.set(description);
  }

  private setTitleUpdater(): void {
    effect(() => {
      const fullTitle = this.fullTitle();

      this.titleService.setTitle(fullTitle);
    });

    effect(() => {
      const metaTitle = this.metaTitle();

      if (metaTitle) {
        this.meta.updateTag({
          name: 'title',
          content: metaTitle,
        });
      }
    });

    effect(() => {
      const metaDescription = this.metaDescription();

      if (metaDescription) {
        this.meta.updateTag({
          name: 'description',
          content: metaDescription,
        });
      }
    });
  }
}
